import { useRef, useEffect, KeyboardEvent, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, AlertCircle, ShieldAlert } from 'lucide-react';
import { Patient } from '../../../domain/Patient';
import { usePatientChat } from '../../../application/usePatientChat';
import { markdownToHtml } from '../../../../../shared/utils/markdown';

interface PatientChatTabProps {
  patient: Patient;
  persist?: boolean;
}

export default function PatientChatTab({ patient, persist = false }: PatientChatTabProps) {
  const { t } = useTranslation();
  const { messages, isLoading, error, sendMessage } = usePatientChat(patient.id, { persist });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!patient.consent_ai_analysis) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
        <ShieldAlert className="w-10 h-10 text-warning-500" />
        <p className="text-base font-medium text-content-secondary">
          {t('patient.chat.consentRequired')}
        </p>
      </div>
    );
  }

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const value = inputRef.current?.value.trim() ?? '';
    if (!value || isLoading) return;
    if (inputRef.current) inputRef.current.value = '';
    await sendMessage(value);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[400px] max-h-[600px]">
      {/* Message list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && !isLoading && (
          <p className="text-center text-sm text-content-tertiary mt-8">
            {t('patient.chat.placeholder')}
          </p>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                msg.role === 'user'
                  ? 'bg-accent text-accent-contrast rounded-br-sm whitespace-pre-wrap'
                  : 'bg-surface-sunken text-content-primary rounded-bl-sm'
              }`}
            >
              {msg.role === 'user' ? (
                msg.content
              ) : (
                <div className="chat-markdown" dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.content) }} />
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-sunken rounded-2xl rounded-bl-sm px-4 py-2">
              <span className="text-sm text-content-tertiary animate-pulse">
                {t('patient.chat.loading')}
              </span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2 bg-status-critical-tint text-status-critical-on-tint text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{t('patient.chat.error')}</span>
          <button
            className="ml-auto underline"
            onClick={() => inputRef.current?.focus()}
          >
            {t('patient.chat.retry')}
          </button>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-2 p-3 border-t border-border-subtle"
      >
        <textarea
          ref={inputRef}
          rows={2}
          maxLength={2000}
          disabled={isLoading}
          onKeyDown={handleKeyDown}
          placeholder={t('patient.chat.placeholder')}
          aria-label={t('patient.chat.placeholder')}
          className="field-base field-default flex-1 resize-none rounded-xl px-3 py-2 min-h-[44px]"
        />
        <button
          type="submit"
          disabled={isLoading}
          aria-label={t('patient.chat.send')}
          className="flex items-center justify-center w-11 h-11 min-h-[44px] min-w-[44px] rounded-xl bg-accent hover:bg-accent-hover disabled:opacity-50 text-accent-contrast transition-colors duration-[--duration-fast]"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
